import {
    takeLatest, put, select, call
} from 'redux-saga/effects';
import { requestShippingMethods, getApplicableShippingRewards } from 'capi';

import { fromJS, List as IList, Map as IMap } from 'immutable';
import { logError, reportPageLoadError } from '../utils/errorUtils';
import { getAddressHash } from '../address/addressModule';
import { getCapiClient } from '../session/sessionSelectors';

import {
    hasProfile,
    getShippingMethodProfileId, getSelectedShippingCartMethod, getSelectedShippingMethod
} from './shippingSelectors';

import {
    createProfileActive,
    createProfileSuccess,
    createProfileFailed,
    storeProfileMethods,
    profileAlreadyExists,
    GET_SHIPPING_METHOD,
    FETCH_SHIPPING_REWARDS, fetchShippingRewards, finishedGettingPromoRewards, setSelectedShippingToken
} from './shippingModule';
import { RECEIVED_PROMO_REWARDS, setPromoRewards, setShippingCostData } from '../promo/promoModule';
import { getShippingPromoData } from '../promo/promoSelectors';
import { updateShippingCostInCart } from '../productCartHandler/productCartModule';
import { promoShippingCostWithData } from './ShippingMethodUtils';
import { getPromoRewards } from '../promo/sharedCartPromoSelectors';

export const CURRENCY_FIELD = 'currencyCode';
export const QUANTITY_FIELD = 'quantity';

export function* createProfileMethods(profile, requestData, shippingRewardPayload, promoCode) {
    yield put(createProfileActive(profile));

    const capiClient = yield select(getCapiClient);
    try {
        if (promoCode) {
            yield put(fetchShippingRewards(capiClient, promoCode, shippingRewardPayload));
        }
        const methods = yield call(requestShippingMethods, capiClient, requestData.toJS());
        const promoData = yield select(getShippingPromoData);

        const shippingCostData = promoShippingCostWithData(methods, promoData);
        yield put(setShippingCostData(shippingCostData));

        yield put(storeProfileMethods(profile, methods));
        yield put(createProfileSuccess(profile, methods, promoData));
    } catch (error) {
        logError(error);
        reportPageLoadError('Error in createProfileMethods', error);
        yield put(createProfileFailed(profile, error.response.status, error.message));
    }
}

export function* getShippingMethodSaga(
    {
        shippingAddress, shippingRewardPayload, promoCode, isNewItem
    }
) {
    let addressHash = shippingAddress.get('addressID', null);
    if (!addressHash) {
        addressHash = yield call(getAddressHash, shippingAddress);
    }

    const profile = getShippingMethodProfileId(
        addressHash,
        shippingAddress.get(QUANTITY_FIELD),
        shippingAddress.get(CURRENCY_FIELD),
    );

    const methodsExist = yield select(hasProfile, profile);
    if (methodsExist) {
        yield put(profileAlreadyExists(profile));

        return;
    }

    yield call(createProfileMethods, profile, shippingAddress, shippingRewardPayload, promoCode, isNewItem);
}

export function* watchGetShippingMethod() {
    yield takeLatest(GET_SHIPPING_METHOD, getShippingMethodSaga);
}

export function* fetchShippingRewardsSaga(action) {
    try {
        const { capiClient, promoCode, orderData } = action.payload;
        const rewards = yield call(getApplicableShippingRewards, capiClient, promoCode, orderData);
        const existingRewards = yield select(getPromoRewards);
        const hasPromotionalData = Object.keys(rewards.promotionalShippingData).length > 0;
        const isDataUpdated = existingRewards
            ? !existingRewards.get('promotionalShippingData', IMap())
                .equals(fromJS(rewards.promotionalShippingData))
            : true;
        if (hasPromotionalData || isDataUpdated) {
            yield put(setPromoRewards(rewards));
        }
    } catch (error) {
        logError(error);
        reportPageLoadError('Error in fetchShippingRewardsSaga', error);
    }
}


export function* watchFetchShippingRewards() {
    yield takeLatest(FETCH_SHIPPING_REWARDS, fetchShippingRewardsSaga);
}

export function* receivedPromoRewardsSaga(action) {
    const { capiClient, requestData } = action.payload;
    try {
        if (capiClient && requestData) {
            const shippingGroup = requestData.get('shippingGroups').get(0);
            if (!shippingGroup) {
                return;
            }
            const { shippingAddress } = shippingGroup;
            const cert = shippingGroup.certs[0];

            const shippingApiPayload = {
                deliveryTime: new Date().toISOString(),
                prevShippingMethod: false,
                deliveryDate: cert.deliveryDate || new Date().toISOString(),
                countryCode: shippingAddress.get('countryCode'),
                message: cert.message || '',
                quantity: cert.quantity || 0,
                city: shippingAddress.get('city'),
                selectedGroupOrdinal: 1,
                street1: shippingAddress.get('street'),
                street2: shippingAddress.get('street2'),
                isSelfBuy: cert.selfBuy === true,
                state: shippingAddress.get('state'),
                hasPlasticNotification: !!cert.recipientEmail,
                accessory: null,
                checkedEmail: cert.checkedEmail || false,
                isPlastic: true,
                amount: cert.faceValue,
                postalCode: shippingAddress.get('zipCode'),
                hasAccessory: false,
                faceplate: cert.faceplateCode,
                isSharedViaLink: cert.isSharedViaLink || false,
                fullName: shippingAddress.get('fullName') || 'DUMMY_NAME',
                currencyCode: requestData.get('currencyCode')
            };
            const methods = yield call(requestShippingMethods, capiClient, shippingApiPayload);
            const promoData = yield select(getShippingPromoData);
            const shippingCostMap = {};
            const selectedShippingCartMethods = yield select(getSelectedShippingCartMethod);
            const updatedMethods = Object.fromEntries(
                Object.entries(methods).map(([key, method]) => {
                    const { token, cost } = method;
                    
                    if (promoData && promoData.get(token) && promoData.get(token, IMap()).get('cartTokens', IList()).size > 0) {
                        shippingCostMap[token] = {
                            actualCost: cost,
                            updatedCost: promoData.get(token).get('updatedCost')
                        };
                        return [key, { ...method, cost: promoData.get(token).get('updatedCost') }];
                    }
                    return [key, method];
                })
            );
            yield put(setShippingCostData(shippingCostMap));
            const cartTokens = new Set(selectedShippingCartMethods.toJS().map(method => method.token));
            const methodsArray = Object.values(updatedMethods);
            const selectedShippingMethod = yield select(getSelectedShippingMethod);
            for (let i = 0; i < methodsArray.length; i++) {
                const method = methodsArray[i];
                if (cartTokens.has(method.token)) {
                    yield put(finishedGettingPromoRewards(method, 'refresh'));
                    yield put(updateShippingCostInCart(method.token, method.cost));
                }
                if (selectedShippingMethod.get('token') === method.token) {
                    yield put(setSelectedShippingToken(fromJS(method)));
                }
            }
        }
    } catch (error) {
        logError(error);
        reportPageLoadError('Error in receivedPromoRewardsSaga', error);
    }
}

export function* watchReceivedPromoRewards() {
    yield takeLatest(RECEIVED_PROMO_REWARDS, receivedPromoRewardsSaga);
}
